/* =====================================================================
 * LiveAgentDock — 에이전트 Dock 바
 *
 * Rebo | 대화 중지/대화하기 | 음소거 | 설정
 * ===================================================================== */

import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  Square,
  Play,
  VolumeX,
  Volume2,
  Settings,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLiveAgentStore } from '@auw/stores';
import { useUIStore } from '@auw/stores';

type LiveStatus = 'idle' | 'connecting' | 'active' | 'disconnected' | 'error';

const ICON_SIZE = 44;
const ICON_GAP = 14;
const DOCK_PAD_X = 16;
const DOCK_PAD_Y = 8;
const ICON_RADIUS = '22.37%';
const LIFT_Y = -14;
const TILT_DEG = 8;
const NEIGHBOR_TILT_DEG = 4;
const ICON_INNER = Math.round(ICON_SIZE * 0.52);

interface DockItem {
  id: string;
  labelKey: string;
  iconSrc?: string;
  iconContain?: boolean;
}

const REBO_ITEM: DockItem = {
  id: 'rebo',
  labelKey: 'liveAgent.agents.Rebo',
  iconSrc: '/assets/helmet.webp',
  iconContain: true,
};

/* ------------------------------------------------------------------ */
/* Animations                                                           */
/* ------------------------------------------------------------------ */

const tooltipIn = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(4px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

const chipFadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const chipPulse = keyframes`
  0%   { transform: scale(1);   opacity: 0.9; }
  50%  { transform: scale(1.5); opacity: 0.35; }
  100% { transform: scale(1);   opacity: 0.9; }
`;

const orbit = keyframes`
  0%   { offset-distance: 0%; }
  100% { offset-distance: 100%; }
`;

const breathing = keyframes`
  0%   { box-shadow: 0 0 0 0    rgba(224,107,80,0.55),
                     0 0 0 0    rgba(224,107,80,0.00); }
  60%  { box-shadow: 0 0 0 6px  rgba(224,107,80,0.10),
                     0 0 22px 6px rgba(224,107,80,0.28); }
  100% { box-shadow: 0 0 0 0    rgba(224,107,80,0.00),
                     0 0 0 0    rgba(224,107,80,0.00); }
`;

const errorShake = keyframes`
  0%,100% { transform: translateX(0); }
  20%     { transform: translateX(-3px); }
  40%     { transform: translateX(3px); }
  60%     { transform: translateX(-2px); }
  80%     { transform: translateX(2px); }
`;

/* Rebo active 진입 + 호흡 (connecting ring/breathing glow 와 같은 모션 언어) */
const reboEntry = keyframes`
  0%   { transform: translateY(0)     scale(1); }
  55%  { transform: translateY(-16px) scale(1.07); }
  100% { transform: translateY(-10px) scale(1.04); }
`;

const reboBreathing = keyframes`
  0%, 100% { transform: translateY(-10px) scale(1.04); }
  50%      { transform: translateY(-13px) scale(1.06); }
`;

/* ------------------------------------------------------------------ */
/* Styled                                                               */
/* ------------------------------------------------------------------ */

const DockWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 0 2px;
  perspective: 800px;
`;

const DockBar = styled.div`
  display: flex;
  align-items: flex-end;
  padding: ${DOCK_PAD_Y}px ${DOCK_PAD_X}px;
  gap: ${ICON_GAP}px;
  border-radius: 22px;
  position: relative;
  overflow: visible;

  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(28, 28, 30, 0.62)'
      : 'rgba(240, 240, 240, 0.55)'};
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid
    ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(255, 255, 255, 0.65)'};
  box-shadow:
    ${({ theme }) =>
      theme.mode === 'dark'
        ? `0 8px 28px rgba(0, 0, 0, 0.4),
           0 2px 8px rgba(0, 0, 0, 0.26),
           inset 0 0.5px 0 rgba(255, 255, 255, 0.04)`
        : `0 8px 32px rgba(0, 0, 0, 0.08),
           0 2px 8px rgba(0, 0, 0, 0.04),
           inset 0 0.5px 0 rgba(255, 255, 255, 0.9)`};
`;

const ItemOuter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  cursor: pointer;
`;

const ItemVisual = styled.div<{
  $active: boolean;
  $hovered: boolean;
  $neighborDir: -1 | 0 | 1;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  transform-style: preserve-3d;
  will-change: transform;
  transition: transform 0.35s cubic-bezier(0.25, 1, 0.5, 1);

  /*
   * $active 일 때는 정적 translateY 대신 "pop-in entry → breathing loop"
   * 체인을 돌린다. 위에서 정의한 connecting orbit / breathing glow 와 같은
   * cubic-bezier·2.4초 리듬을 공유해 dock 전체가 같은 호흡으로 움직인다.
   * hover / neighbor 상태는 animation 이 없을 때만 transform 으로 제어한다.
   */
  transform: ${({ $hovered, $neighborDir, $active }) => {
    if ($active) return 'none';
    if ($hovered) return `translateY(${LIFT_Y}px) rotateX(-${TILT_DEG}deg) scale(1.02)`;
    if ($neighborDir !== 0)
      return `translateY(${LIFT_Y * 0.25}px) rotateY(${$neighborDir * NEIGHBOR_TILT_DEG}deg)`;
    return 'translateY(0) rotateX(0)';
  }};

  ${({ $active }) =>
    $active &&
    css`
      animation:
        ${reboEntry} 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
        ${reboBreathing} 2.4s ease-in-out 0.55s infinite;
    `}
`;

const IconSquare = styled.div<{ $hovered: boolean; $active: boolean }>`
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  border-radius: ${ICON_RADIUS};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  color: ${({ theme, $active }) =>
    $active
      ? '#E06B50'
      : theme.mode === 'dark' ? '#bdbdc2' : '#3a3a3c'};

  transition: color 0.25s ease,
    box-shadow 0.35s cubic-bezier(0.25, 1, 0.5, 1),
    filter 0.35s ease-out;

  background: ${({ theme, $active }) =>
    $active
      ? theme.mode === 'dark'
        ? 'linear-gradient(145deg, #444447 0%, #343437 100%)'
        : 'linear-gradient(145deg, #ffffff 0%, #f5f5f7 100%)'
      : theme.mode === 'dark'
        ? 'linear-gradient(145deg, #343436 0%, #28282a 100%)'
        : 'linear-gradient(145deg, #ffffff 0%, #f5f5f7 100%)'};

  box-shadow: ${({ theme, $hovered, $active }) =>
    $hovered || $active
      ? theme.mode === 'dark'
        ? `0 4px 12px rgba(0, 0, 0, 0.34),
           0 12px 30px rgba(0, 0, 0, 0.28),
           inset 0 0.5px 0 rgba(255, 255, 255, 0.05)`
        : `0 4px 12px rgba(0, 0, 0, 0.1),
           0 16px 40px rgba(0, 0, 0, 0.08),
           0 0 0 0.5px rgba(0, 0, 0, 0.04)`
      : theme.mode === 'dark'
        ? `0 2px 6px rgba(0, 0, 0, 0.32),
           0 8px 20px rgba(0, 0, 0, 0.22),
           inset 0 0.5px 0 rgba(255, 255, 255, 0.04)`
        : `0 1px 3px rgba(0, 0, 0, 0.06),
           0 6px 20px rgba(0, 0, 0, 0.05),
           0 0 0 0.5px rgba(0, 0, 0, 0.04)`};

  & > svg {
    display: block;
    flex-shrink: 0;
    backface-visibility: hidden;
    transform: translateZ(0);
    shape-rendering: geometricPrecision;
  }
`;

const IconImg = styled.img<{ $contain?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $contain }) => ($contain ? 'contain' : 'cover')};
  border-radius: inherit;
  padding: ${({ $contain }) => ($contain ? '5px' : '0')};
`;

const TooltipBubble = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 24px);
  left: 50%;
  transform: translateX(-50%);
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;

  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#1c1c1e' : '#18181b'};
  color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.14)'};

  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  animation: ${tooltipIn} 0.15s ease-out;
`;

const DockReflection = styled.div`
  width: 65%;
  height: 2px;
  margin-top: 2px;
  border-radius: 2px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(0,0,0,0.03), transparent)'};
`;

const Divider = styled.div`
  width: 1px;
  height: ${ICON_SIZE * 0.6}px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  align-self: center;
  flex-shrink: 0;
`;

/* ─── Status chip (dock 위에 떠 있는 pill) ─── */

const StatusChip = styled.div<{ $tone: 'accent' | 'neutral' | 'warn' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 12px;
  margin-bottom: 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.01em;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
  animation: ${chipFadeIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1);

  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.92)' : 'rgba(28,28,30,0.88)'};

  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(28, 28, 30, 0.62)'
      : 'rgba(255, 255, 255, 0.72)'};
  backdrop-filter: blur(22px) saturate(180%);
  -webkit-backdrop-filter: blur(22px) saturate(180%);

  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === 'warn'
        ? 'rgba(255, 149, 0, 0.35)'
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.10)'
          : 'rgba(255,255,255,0.70)'};

  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 6px 20px rgba(0,0,0,0.35), inset 0 0.5px 0 rgba(255,255,255,0.05)'
      : '0 6px 20px rgba(0,0,0,0.08), inset 0 0.5px 0 rgba(255,255,255,0.9)'};

  ${({ $tone }) =>
    $tone === 'warn' &&
    css`
      color: #c05621;
    `}
`;

const ChipDot = styled.span<{ $tone: 'accent' | 'neutral' | 'warn' }>`
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $tone }) =>
    $tone === 'warn' ? '#ff9500' : $tone === 'accent' ? '#E06B50' : '#999'};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: inherit;
    animation: ${chipPulse} 1.6s ease-in-out infinite;
  }
`;

/* ─── Ambient status overlay (stopResume 아이콘 위에 겹침) ───
 *
 * `IconSquare`는 border-radius 유지를 위해 `overflow: hidden`이다. 외곽으로
 * 퍼지는 glow/ring 이 잘리지 않도록 아래의 래퍼(`IconAmbientWrap`)로 한 겹
 * 감싸서 오버레이/배지를 `IconSquare`의 형제로 렌더한다.
 */

const IconAmbientWrap = styled.div`
  position: relative;
  overflow: visible;
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  border-radius: ${ICON_RADIUS};
  flex-shrink: 0;
`;

const IconStatusOverlay = styled.div<{ $status: LiveStatus }>`
  position: absolute;
  inset: -3px;
  border-radius: calc(${ICON_RADIUS} + 2px);
  pointer-events: none;
  z-index: 2;

  ${({ $status }) => {
    if ($status === 'connecting') {
      /*
       * offset-path 로 squircle 둘레를 정의하고, 두 개의 glow dot 이 서로
       * 반대편(0s / -0.7s)에서 같은 경로를 순회한다. 원형 ring 이 아니라
       * IconSquare 의 rounded-square 실루엣을 따라 돌기 때문에 dock 과
       * 시각적으로 달라붙는다. `offset-path: inset(...)` 는 Chromium 116+,
       * Firefox 117+, Safari 16+ 에서 지원된다.
       */
      return css`
        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: radial-gradient(
            circle,
            rgba(224, 107, 80, 1) 0%,
            rgba(224, 107, 80, 0.6) 45%,
            rgba(224, 107, 80, 0) 78%
          );
          box-shadow:
            0 0 10px rgba(224, 107, 80, 0.85),
            0 0 20px rgba(224, 107, 80, 0.45);
          offset-path: inset(0 round ${ICON_RADIUS});
          offset-anchor: center;
          offset-rotate: 0deg;
          animation: ${orbit} 1.4s linear infinite;
        }
        &::after {
          width: 7px;
          height: 7px;
          opacity: 0.55;
          filter: blur(0.5px);
          animation-delay: -0.7s;
        }
      `;
    }
    if ($status === 'active') {
      return css`
        animation: ${breathing} 2.4s ease-in-out infinite;
      `;
    }
    if ($status === 'error') {
      return css`
        box-shadow:
          0 0 0 2px rgba(255, 59, 48, 0.4),
          0 0 18px 2px rgba(255, 59, 48, 0.3);
        animation: ${errorShake} 0.45s ease-in-out 2;
      `;
    }
    return css`
      display: none;
    `;
  }}
`;

const ErrorBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff3b30;
  box-shadow: 0 0 0 2px
    ${({ theme }) => (theme.mode === 'dark' ? '#1c1c1e' : '#ffffff')};
  z-index: 3;
`;

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

function getNeighborDir(hoveredIdx: number | null, idx: number): -1 | 0 | 1 {
  if (hoveredIdx === null) return 0;
  if (idx === hoveredIdx - 1) return -1;
  if (idx === hoveredIdx + 1) return 1;
  return 0;
}

export const LiveAgentDock: React.FC = () => {
  const { t } = useTranslation('common');
  const activeAgent = useLiveAgentStore((s) => s.activeAgent);
  const agentSpeaking = useLiveAgentStore((s) => s.agentSpeaking);
  const sessionStatus = useLiveAgentStore((s) => s.sessionStatus) as LiveStatus;
  const muted = useLiveAgentStore((s) => s.muted);
  const stopSessionFn = useLiveAgentStore((s) => s.stopSessionFn);
  const resumeSessionFn = useLiveAgentStore((s) => s.resumeSessionFn);
  const setMuted = useLiveAgentStore((s) => s.setMuted);
  const setSettingsOpen = useLiveAgentStore((s) => s.setSettingsOpen);
  const addToast = useUIStore((s) => s.addToast);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const isSessionActive = sessionStatus === 'active';
  const isStopped = sessionStatus === 'disconnected' || sessionStatus === 'idle';

  // 이벤트성 상태 전이를 감지해 전역 토스트로 안내한다.
  //   · connecting → active : "Rebo 준비 완료" (success, 2.5s)
  //   · active     → error  : "연결이 잠시 끊겼어요" (warning, 5s)
  // 의도적인 정지(active → disconnected)는 사용자가 dock 버튼을 누른 결과이므로
  // 조용하게 처리한다.
  const prevStatusRef = useRef<LiveStatus>(sessionStatus);
  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = sessionStatus;

    if (prev !== curr) {
      if (prev === 'connecting' && curr === 'active') {
        addToast({
          type: 'success',
          title: t('liveAgent.toast.readyTitle'),
          description: t('liveAgent.toast.readyDesc'),
          duration: 2500,
        });
      } else if (curr === 'error') {
        addToast({
          type: 'warning',
          title: t('liveAgent.toast.disconnectTitle'),
          description: t('liveAgent.toast.disconnectDesc'),
          duration: 5000,
        });
      }
      prevStatusRef.current = curr;
    }
  }, [sessionStatus, addToast, t]);

  // dock 위에 anchored 된 chip은 "사용자가 기다려야 하는 지속 상태"에서만 표시한다.
  // 나머지는 dock 아이콘의 ambient overlay만으로 충분하다.
  const chipLabel =
    sessionStatus === 'connecting'
      ? t('liveAgent.chip.connecting')
      : null;
  const chipTone: 'accent' | 'neutral' | 'warn' = 'accent';

  const items = [
    { id: 'rebo', label: t(REBO_ITEM.labelKey) },
    { id: 'stopResume', label: isSessionActive ? t('liveAgent.dock.pause') : t('liveAgent.dock.play') },
    { id: 'mute', label: muted ? t('liveAgent.dock.unmute') : t('liveAgent.dock.mute') },
    { id: 'settings', label: t('liveAgent.dock.settings') },
  ];

  const handleClick = (id: string) => {
    if (id === 'stopResume') {
      if (isSessionActive) {
        stopSessionFn?.();
      } else if (isStopped) {
        resumeSessionFn?.();
      }
    }
    if (id === 'mute') setMuted(!muted);
    if (id === 'settings') setSettingsOpen(true);
  };

  const renderIcon = (id: string) => {
    switch (id) {
      case 'rebo':
        return (
          <IconImg
            src={REBO_ITEM.iconSrc!}
            alt="Rebo"
            draggable={false}
            $contain={REBO_ITEM.iconContain}
          />
        );
      case 'stopResume':
        return isSessionActive
          ? <Square size={ICON_INNER} strokeWidth={1.7} absoluteStrokeWidth />
          : <Play size={ICON_INNER} strokeWidth={1.7} absoluteStrokeWidth />;
      case 'mute':
        return muted
          ? <VolumeX size={ICON_INNER} strokeWidth={1.7} absoluteStrokeWidth />
          : <Volume2 size={ICON_INNER} strokeWidth={1.7} absoluteStrokeWidth />;
      case 'settings':
        return <Settings size={ICON_INNER} strokeWidth={1.7} absoluteStrokeWidth />;
      default:
        return null;
    }
  };

  return (
    <DockWrapper>
      {chipLabel && (
        <StatusChip $tone={chipTone} role="status" aria-live="polite">
          <ChipDot $tone={chipTone} />
          {chipLabel}
        </StatusChip>
      )}
      <DockBar onMouseLeave={() => setHoveredIdx(null)}>
        {items.map((item, idx) => {
          const isRebo = item.id === 'rebo';
          /*
           * Rebo 의 lift/breathing 애니메이션은 "에이전트가 실제로 발화 중"일
           * 때만 발동한다. 세션이 active 상태여도 사용자 턴이거나 tool 호출
           * 대기 등 침묵 구간에는 기본 위치를 유지해 정적 톤을 지킨다.
           * `agentSpeaking` 은 `useLiveAgentSession.feedAudio()` 의 audio
           * chunk 기반 debounce 로 갱신된다.
           */
          const isActive = isRebo && activeAgent === 'Rebo' && agentSpeaking;
          const isHovered = hoveredIdx === idx;
          const neighborDir = getNeighborDir(hoveredIdx, idx);
          const isStopResume = item.id === 'stopResume';
          const showStatusOverlay =
            isStopResume &&
            (sessionStatus === 'connecting' ||
              sessionStatus === 'active' ||
              sessionStatus === 'error');

          return (
            <React.Fragment key={item.id}>
              {idx === 1 && <Divider />}
              <ItemOuter
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={() => handleClick(item.id)}
              >
                <TooltipBubble $visible={isHovered}>
                  {item.label}
                </TooltipBubble>
                <ItemVisual
                  $active={isActive}
                  $hovered={isHovered}
                  $neighborDir={neighborDir}
                >
                  {isStopResume ? (
                    <IconAmbientWrap>
                      <IconSquare $hovered={isHovered} $active={isActive}>
                        {renderIcon(item.id)}
                      </IconSquare>
                      {showStatusOverlay && (
                        <IconStatusOverlay $status={sessionStatus} />
                      )}
                      {sessionStatus === 'error' && <ErrorBadge />}
                    </IconAmbientWrap>
                  ) : (
                    <IconSquare $hovered={isHovered} $active={isActive}>
                      {renderIcon(item.id)}
                    </IconSquare>
                  )}
                </ItemVisual>
              </ItemOuter>
            </React.Fragment>
          );
        })}
      </DockBar>
      <DockReflection />
    </DockWrapper>
  );
};
